import React, { useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import ChipMui from '@mui/material/Chip';
import without from 'lodash/without';
import omit from 'lodash/omit';

import FilterDropDown from './FilterDropDown';
import { useSearch, queryToSearch } from 'shared/components/makeNav';

export const FilterList = ({ filters, rows, onFilterValue }) => {
  const { push } = useHistory();
  const { pathname } = useLocation();

  const handleClear = useCallback(() => {
    push(pathname);
  }, [push, pathname]);
  const query = useSearch();

  return (
    <Container id="h-more">
      <Table>
        <Heading>
          <span>Filter by:</span>
          <span onClick={handleClear}>Clear filters</span>
        </Heading>
        <TableRow>
          <div>
            <FilterDropDown
              name="portfolio"
              buttonTitle="Portfolio"
              {...{ rows }}
              field="name"
              onValue={onFilterValue}
              width={260}
            />
          </div>
          <div>
            <FilterDropDown
              name="npo"
              buttonTitle="Nonprofit (NPO)"
              {...{ rows }}
              field="grantee.name"
              onValue={onFilterValue}
              width={260}
            />
          </div>
          <div>
            <FilterDropDown
              name="manager"
              buttonTitle="Manager"
              {...{ rows }}
              field="investment_relationship_full_name"
              onValue={onFilterValue}
              width={260}
            />
          </div>
          <div>
            <FilterDropDown
              name="status"
              buttonTitle="Report status"
              {...{ rows }}
              field="combinedStatus"
              onValue={onFilterValue}
              sorted
              keyId={keyIdForStatus}
              width={260}
            />
          </div>
          <div>
            <FilterDropDown
              name="stage"
              buttonTitle="Stage"
              {...{ rows }}
              field="reportStage"
              onValue={onFilterValue}
              sorted
              keyId={keyIdForStage}
              width={260}
            />
          </div>
        </TableRow>

        <TableRow>
          <div>
            <FilterDropDown
              name="service"
              buttonTitle="Impact Model"
              {...{ rows }}
              field="report.service"
              onValue={onFilterValue}
              width={260}
            />
          </div>
          {/* <div>
          <FilterDropDown
            name="donation-size"
            buttonTitle="Contribution Size"
            {...{ rows }}
            field="amountCategory5"
            onValue={onFilterValue}
            sorted
            keyId={keyIdForAmountCategory5}
            width={260}
          />
        </div> */}
          <div>
            <FilterDropDown
              name="year"
              buttonTitle="Year"
              {...{ rows }}
              field="year"
              onValue={onFilterValue}
              width={260}
            />
          </div>
          <div>
            <FilterDropDown
              name="archive"
              buttonTitle="Archive"
              {...{ rows }}
              field="portfolioStatus"
              onValue={onFilterValue}
              width={260}
            />
          </div>
          <div>
            <div></div>
          </div>
        </TableRow>
        <TableRow>
          <Filters>
            {filters
              ?.filter(({ value }) => value)
              ?.map(({ filter, value }) => (
                <Chip
                  key={`${filter}=${value}`}
                  variant="outlined"
                  label={`${filterLabel[filter]}: ${value}`}
                  onDelete={() => {
                    const updated = removeFromSearch(query, filter, value);
                    push(`${pathname}?${updated}`);
                  }}
                />
              ))}
          </Filters>
        </TableRow>
      </Table>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: auto;
`;

const Table = styled.div`
  width: 100%;
  height: auto;
  min-height: 100px;
  background-color: #46586b;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
`;

const Heading = styled.div`
  position: relative;
  padding: 16px;
  padding-bottom: 0px;
  padding-top: 10px;
  display: flex;
  justify-content: space-between;
  span {
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
    &:last-of-type {
      text-decoration: underline;
      cursor: pointer;
    }
  }
`;

const TableRow = styled.div`
  width: 100%;
  height: auto;
  padding: 10px;
  background-color: transparent;
  display: flex;
  flex-direction: row;
  padding-top: 4px;
  padding-bottom: 0px;
  &:last-of-type {
    padding-top: 0px;
    padding-bottom: 10px;
  }
  div {
    flex: 1;
    padding: 6px;
    button.MuiButtonBase-root {
      width: 100%;
      border: none !important;
      border-top-left-radius: 3px !important;
      border-bottom-left-radius: 3px !important;
      background-color: #ffffff !important;
      height: 40px;
      span {
        justify-content: space-between;
        color: #46586b;
      }
    }
  }
`;

/* CONSTANTS */

const statuses = [
  'Unnotified',
  'Sending',
  'Waiting',
  'Allocated',
  'Returned',
  'Rejected',
  'Application',
  'Scheduled',
  'Draft',
  'Awaiting TI Feedback',
  'TI Feedback Provided',
  'Awaiting TI Verification',
  'Awaiting Funder Feedback',
  'Funder Feedback Provided',
  'Awaiting Funder Verification',
  'Published',
  'Finalized',
].map(s => s.toLowerCase());
const amountCategories = [
  'Below $5K',
  'Below $10K',
  'Below $25K',
  'Below $100K',
  'Above $100K',
  'Below $250K',
  'Above $250K',
];
const stages = [
  'Unallocated',
  'Draft',
  'Initial Report',
  'Interim Report',
  'Final Report',
].map(s => s.toLowerCase());
const keyIdForStatus = status => statuses.indexOf(status.toLowerCase());
const keyIdForStage = stage => stages.indexOf(stage.toLowerCase());
const keyIdForAmountCategory5 = cat => amountCategories.indexOf(cat);

function removeFromSearch(query, filter, value) {
  const after = without(JSON.parse(query[filter]), value);
  return after.length
    ? queryToSearch({
        ...query,
        [filter]: encodeURIComponent(JSON.stringify(after)),
      })
    : queryToSearch(omit(query, filter));
}

const filterLabel = {
  npo: 'NPO',
  portfolio: 'Portfolio',
  manager: 'Manager',
  status: 'Status',
  stage: 'Stage',
  service: 'Impact Model',
  // 'donation-size': 'Contribution Size',
  year: 'Year',
  archive: 'Archive',
};

const Chip = styled(ChipMui)`
  margin-top: 5px;
`;

const Filters = styled.div`
  .MuiChip-root {
    background-color: white !important;
    margin-right: 5px !important;
  }
`;

export default FilterList;
